import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Cookies from "js-cookie";
import CONSTANTS from "../utils/constants";
import ProductsPagination from "./Pagination.jsx";
import { useTranslation } from "react-i18next";

export default function AdminProducts() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [mainCategories, setMainCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [userPermissions, setUserPermissions] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [productsPerPage] = useState(20);
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [mainCategory, setMainCategory] = useState("");
  const [subCategory, setSubCategory] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [available, setAvailable] = useState(true);
  const [minPriceTemp, setMinPriceTemp] = useState("");
  const [maxPriceTemp, setMaxPriceTemp] = useState("");
  const [mainCategoryTemp, setMainCategoryTemp] = useState("");
  const [subCategoryTemp, setSubCategoryTemp] = useState("");
  const [searchTermTemp, setSearchTermTemp] = useState("");
  const [availableTemp, setAvailableTemp] = useState(true);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [sortColumn, setSortColumn] = useState("updated_at"); // Default to updated_at
  const [sortDirection, setSortDirection] = useState("desc"); // Default desc
  const fileInputRef = useRef(null);
  const { t, i18n } = useTranslation();

  const getStockColor = (stock) => {
    const stockNumber = parseFloat(stock);
    if (stockNumber < 10) {
      return "danger";
    } else if (stockNumber >= 10 && stockNumber <= 20) {
      return "warning";
    } else {
      return "success";
    }
  };

  const getStockIcon = (stock) => {
    const stockNumber = parseFloat(stock);
    if (stockNumber < 10) {
      return "bi-exclamation-triangle";
    } else if (stockNumber >= 10 && stockNumber <= 20) {
      return "bi-info-circle";
    } else {
      return "bi-check-circle";
    }
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("desc");
    }
    setCurrentPage(1); // Reset to first page on sort
  };

  // Load filters from cookies on mount
  useEffect(() => {
    const loadFilters = () => {
      const minP = Cookies.get('filter_minPrice');
      if (minP !== undefined) {
        setMinPrice(minP);
        setMinPriceTemp(minP);
      }

      const maxP = Cookies.get('filter_maxPrice');
      if (maxP !== undefined) {
        setMaxPrice(maxP);
        setMaxPriceTemp(maxP);
      }

      const mainC = Cookies.get('filter_mainCategory');
      if (mainC !== undefined) {
        setMainCategory(mainC);
        setMainCategoryTemp(mainC);
      }

      const subC = Cookies.get('filter_subCategory');
      if (subC !== undefined) {
        setSubCategory(subC);
        setSubCategoryTemp(subC);
      }

      const searchT = Cookies.get('filter_searchTerm');
      if (searchT !== undefined) {
        setSearchTerm(searchT);
        setSearchTermTemp(searchT);
      }

      const avail = Cookies.get('filter_available');
      if (avail !== undefined) {
        const bool = avail === 'true';
        setAvailable(bool);
        setAvailableTemp(bool);
      }
    };

    loadFilters();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const user = Cookies.get("user")
          ? JSON.parse(Cookies.get("user"))
          : null;
        if (!user?.id) {
          navigate("/admin");
          return;
        }
        const permissionsResponse = await axios.get(
          `${CONSTANTS.baseRoute}/permissions`,
          {
            params: { user_id: user.id },
          }
        );
        const permissions = permissionsResponse.data.permissions;
        setUserPermissions(permissions);
        if (!permissions.includes(2)) {
          setMessage({
            type: "danger",
            text: t('noPermissionView'),
          });
          navigate("/admin/home");
          return;
        }
        const [productResponse, catResponse] = await Promise.all([
          axios.get(`${CONSTANTS.baseRoute}/products/admin`),
          axios.get(`${CONSTANTS.baseRoute}/categories`),
        ]);
        const allCategories = catResponse.data;
        const mainCats = allCategories
          .filter((cat) => cat.parent_id === null)
          .map((cat) => ({ id: cat.id, name: cat.name_tm }))
          .sort((a, b) => a.name.localeCompare(b.name));
        const subCats = allCategories
          .filter((cat) => cat.parent_id !== null)
          .map((cat) => ({
            id: cat.id,
            name: cat.name_tm,
            parent_id: cat.parent_id,
          }))
          .sort((a, b) => a.name.localeCompare(b.name));
        setMainCategories(mainCats);
        setSubCategories(subCats);
        const enrichedProducts = productResponse.data.map((product) => {
          return {
            ...product,
            main_category_name: product.main_category_name || t('unknownCategory'),
            subcategory_name: product.subcategory_name || "-",
          };
        });
        setProducts(enrichedProducts);
        setCurrentPage(1);
      } catch (error) {
        console.error("Harytlar çekilemedi:", error);
        setMessage({
          type: "danger",
          text: error.response?.data?.error || t('loadError'),
        });
        if (error.response?.status === 401 || error.response?.status === 403) {
          navigate("/admin");
        }
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [navigate, t]);

  const handleSearch = () => {
    setMinPrice(minPriceTemp);
    setMaxPrice(maxPriceTemp);
    setMainCategory(mainCategoryTemp);
    setSubCategory(subCategoryTemp);
    setSearchTerm(searchTermTemp);
    setAvailable(availableTemp);
    setCurrentPage(1);

    // Save to cookies
    Cookies.set('filter_minPrice', minPriceTemp);
    Cookies.set('filter_maxPrice', maxPriceTemp);
    Cookies.set('filter_mainCategory', mainCategoryTemp);
    Cookies.set('filter_subCategory', subCategoryTemp);
    Cookies.set('filter_searchTerm', searchTermTemp);
    Cookies.set('filter_available', availableTemp.toString());
  };

  const handleClear = () => {
    setMinPriceTemp("");
    setMaxPriceTemp("");
    setMainCategoryTemp("");
    setSubCategoryTemp("");
    setSearchTermTemp("");
    setAvailableTemp(true);
    setMinPrice("");
    setMaxPrice("");
    setMainCategory("");
    setSubCategory("");
    setSearchTerm("");
    setAvailable(true);
    setCurrentPage(1);

    // Clear cookies
    ['minPrice', 'maxPrice', 'mainCategory', 'subCategory', 'searchTerm', 'available'].forEach(key => {
      Cookies.remove(`filter_${key}`);
    });
  };

  const handleMainCategoryChange = (e) => {
    const selectedName = e.target.value;
    setMainCategoryTemp(selectedName);
    setSubCategoryTemp(""); // Reset subcategory when main changes
  };

  const handleEdit = (productId) => {
    if (!userPermissions.includes(4)) {
      setMessage({
        type: "warning",
        text: t('noPermissionEdit'),
      });
      setTimeout(() => setMessage({ type: "", text: "" }), 3000);
      return;
    }
    navigate(`/admin/home/products/edit/${productId}`);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === "application/json") {
      setImportFile(file);
      setMessage({ type: "", text: "" });
    } else if (file) {
      setImportFile(null);
      setMessage({
        type: "danger",
        text: t('invalidFileType'),
      });
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      setMessage({ type: "danger", text: t('selectFile') });
      return;
    }
    setIsImporting(true);
    setMessage({ type: "", text: "" });
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const importedData = JSON.parse(e.target.result);
        if (!Array.isArray(importedData)) {
          throw new Error(t('invalidJsonArray'));
        }
        let token = Cookies.get("token") || Cookies.get("access_token");
        const headers = {
          "Content-Type": "application/json",
        };
        if (token) {
          if (!token.startsWith("Bearer ")) {
            token = `Bearer ${token}`;
          }
          headers["Authorization"] = token;
          headers["TOKEN"] = token.replace("Bearer ", "");
          headers["token"] = token.replace("Bearer ", "");
        }
        const config = {
          headers,
        };
        const productsToSync = [];
        const updatedProductsState = [...products];
        const existingBarcodes = new Set(products.map((p) => p.barcode));
        for (const item of importedData) {
          const syncProduct = {
            barcode: item.barcode || item.Barcode || item.BARCODE,
            quantity:
              item.quantity || item.Quantity || item.stock || item.QUANTITY,
            unitPrice:
              item.unitPrice ||
              item.UnitPrice ||
              item.sellingPrice ||
              item.price ||
              item.PRICE,
            unitMeasureId:
              item.unitMeasureId ||
              item.UnitMeasureId ||
              item.unit_type ||
              item.UNIT_TYPE,
            name: item.name || item.Name || item.productName || item.NAME,
          };
          if (
            !syncProduct.barcode ||
            syncProduct.barcode === "null" ||
            syncProduct.unitPrice === undefined ||
            syncProduct.unitPrice === null ||
            syncProduct.unitPrice === "null"
          ) {
            console.warn(`Eksik alanlar, ürün atlandı: ${syncProduct.barcode}`);
            continue;
          }
          const formattedProduct = {
            barcode: String(syncProduct.barcode).trim(),
            quantity:
              syncProduct.quantity !== undefined &&
              syncProduct.quantity !== null &&
              syncProduct.quantity !== "null"
                ? String(syncProduct.quantity)
                : undefined,
            unitPrice: String(syncProduct.unitPrice).trim(),
            unitMeasureId:
              syncProduct.unitMeasureId !== undefined &&
              syncProduct.unitMeasureId !== null &&
              syncProduct.unitMeasureId !== "null"
                ? String(syncProduct.unitMeasureId)
                : undefined,
            name:
              syncProduct.name && syncProduct.name !== "null"
                ? String(syncProduct.name).trim()
                : undefined,
          };
          productsToSync.push(formattedProduct);
          const productIndex = updatedProductsState.findIndex(
            (p) => p.barcode === formattedProduct.barcode
          );
          if (productIndex !== -1) {
            updatedProductsState[productIndex] = {
              ...updatedProductsState[productIndex],
              price: parseFloat(formattedProduct.unitPrice) || 0,
              purchase_limit: formattedProduct.quantity
                ? parseInt(formattedProduct.quantity)
                : null,
              unit_type: formattedProduct.unitMeasureId
                ? parseInt(formattedProduct.unitMeasureId)
                : null,
              name_tm:
                formattedProduct.name ||
                updatedProductsState[productIndex].name_tm,
              name_ru:
                formattedProduct.name ||
                updatedProductsState[productIndex].name_ru,
              updated_at: new Date().toISOString(),
            };
          } else {
            const newProduct = {
              id: `temp-${Date.now()}-${Math.random()}`,
              barcode: formattedProduct.barcode,
              name_tm:
                formattedProduct.name || `${t('product')}-${formattedProduct.barcode}`,
              name_ru:
                formattedProduct.name || `${t('product')}-${formattedProduct.barcode}`,
              price: parseFloat(formattedProduct.unitPrice) || 0,
              purchase_limit: formattedProduct.quantity
                ? parseInt(formattedProduct.quantity)
                : null,
              unit_type: formattedProduct.unitMeasureId
                ? parseInt(formattedProduct.unitMeasureId)
                : null,
              is_active: true,
              category_id: 1,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            };
            updatedProductsState.push(newProduct);
          }
        }
        if (productsToSync.length > 0) {
          const response = await axios.put(
            `${CONSTANTS.baseRoute}/synchronize/products`,
            {
              products: productsToSync,
            },
            config
          );
          console.log("Sync response:", response.data);
          if (response.data.details) {
            response.data.details.forEach((result) => {
              if (result.success && result.productId) {
                const productIndex = updatedProductsState.findIndex(
                  (p) =>
                    p.barcode === result.barcode &&
                    p.id.toString().includes("temp-")
                );
                if (productIndex !== -1) {
                  updatedProductsState[productIndex].id = result.productId;
                }
              }
            });
          }
          setProducts(updatedProductsState);
          let messageText = "";
          if (response.data.updated > 0 && response.data.inserted > 0) {
            messageText = t('updatedAndInserted', { updated: response.data.updated, inserted: response.data.inserted });
          } else if (response.data.updated > 0) {
            messageText = t('updated', { count: response.data.updated });
          } else if (response.data.inserted > 0) {
            messageText = t('inserted', { count: response.data.inserted });
          } else {
            messageText = t('noChanges');
          }
          if (response.data.failed > 0) {
            messageText += ` ${t('failed', { count: response.data.failed })}`;
          }
          setMessage({
            type: "success",
            text: messageText,
          });
        } else {
          setMessage({
            type: "warning",
            text: t('noProductsToProcess'),
          });
        }
      } catch (error) {
        console.error("Import säwligi:", error);
        let errorMessage = t('importError');
        if (error.response) {
          if (error.response.status === 403) {
            if (error.response.data?.error === "Missing token") {
              errorMessage = t('missingToken');
            } else if (error.response.data?.error === "Invalid token") {
              errorMessage = t('invalidToken');
            } else {
              errorMessage = t('noPermissionImport');
            }
          } else if (error.response.status === 400) {
            errorMessage = error.response.data?.error || t('invalidDataFormat');
          } else {
            errorMessage = error.response.data?.message || errorMessage;
          }
        } else if (error.message) {
          errorMessage = error.message;
        }
        setMessage({ type: "danger", text: errorMessage });
      } finally {
        setIsImporting(false);
        setImportFile(null);
        if (fileInputRef.current) {
          try {
            fileInputRef.current.value = "";
          } catch (err) {
            console.warn("File input reset error:", err);
          }
        }
        setShowImportModal(false);
      }
    };
    reader.onerror = (error) => {
      console.error("File read error:", error);
      setMessage({
        type: "danger",
        text: t('fileReadError'),
      });
      setIsImporting(false);
      setImportFile(null);
      setShowImportModal(false);
    };
    reader.readAsText(importFile);
  };

  const filteredAndSortedProducts = useMemo(() => {
    let filtered = products.filter((product) => {
      const price = parseFloat(product.price);
      if (minPrice && price < parseFloat(minPrice)) return false;
      if (maxPrice && price > parseFloat(maxPrice)) return false;
      if (mainCategory && product.main_category_name !== mainCategory) {
        return false;
      }
      if (subCategory && product.subcategory_name !== subCategory) {
        return false;
      }
      if (product.is_active !== available) return false;
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        if (
          !product.name_tm.toLowerCase().includes(searchLower) &&
          !String(product.barcode || "")
            .toLowerCase()
            .includes(searchLower)
        ) {
          return false;
        }
      }
      return true;
    });
    // Apply sorting
    if (sortColumn === "purchase_limit") {
      filtered.sort((a, b) => {
        const aVal = parseFloat(a.purchase_limit) || 0;
        const bVal = parseFloat(b.purchase_limit) || 0;
        return sortDirection === "desc" ? bVal - aVal : aVal - bVal;
      });
    } else {
      // Sort by sortColumn (date field), then by the other date field
      const otherColumn = sortColumn === "created_at" ? "updated_at" : "created_at";
      filtered.sort((a, b) => {
        let aVal = new Date(a[sortColumn] || "1970-01-01");
        let bVal = new Date(b[sortColumn] || "1970-01-01");
        let comparison = 0;
        if (sortDirection === "desc") {
          comparison = bVal - aVal;
        } else {
          comparison = aVal - bVal;
        }
        if (comparison === 0) {
          // Secondary sort by the other date field, desc
          aVal = new Date(a[otherColumn] || "1970-01-01");
          bVal = new Date(b[otherColumn] || "1970-01-01");
          comparison = bVal - aVal;
        }
        return comparison;
      });
    }
    return filtered;
  }, [
    products,
    minPrice,
    maxPrice,
    mainCategory,
    subCategory,
    available,
    searchTerm,
    sortColumn,
    sortDirection,
  ]);

  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProducts = filteredAndSortedProducts.slice(
    indexOfFirstProduct,
    indexOfLastProduct
  );
  const totalPages = Math.ceil(filteredAndSortedProducts.length / productsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const selectedMainId = mainCategories.find(
    (cat) => cat.name === mainCategoryTemp
  )?.id;
  const filteredSubCategories = subCategories.filter(
    (sub) => sub.parent_id === selectedMainId
  );

  const getSortIcon = (column) => {
    if (sortColumn !== column) return null;
    return sortDirection === "asc" ? "bi-chevron-up" : "bi-chevron-down";
  };

  if (loading) {
    return (
      <div className="admin-main p-4 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">{t('loading')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-main p-2">
      <div className="d-flex justify-content-between align-items-center mb-1">
        <div className="d-flex gap-2 align-items-center">
          <button className="btn btn-light" onClick={() => navigate(-1)}>
            <i className="bi bi-chevron-left"></i>
          </button>
          <button className="btn btn-light" onClick={() => navigate(+1)}>
            <i className="bi bi-chevron-right"></i>
          </button>
          <h5 className="fw-bold mb-0">{t('title')}</h5>
        </div>
        <div className="d-flex gap-2">
          {userPermissions.includes(3) && (
            <div className="d-flex gap-2">
              <button
                className="btn btn-outline-success d-flex gap-2 align-items-center"
                onClick={() => setShowImportModal(true)}
              >
                <i className="bi bi-file-earmark-arrow-down-fill"></i>{t('import')}
              </button>
              <button
                className="btn btn-outline-primary d-flex gap-2 align-items-center"
                onClick={() => navigate("/admin/home/products/add")}
              >
                <i className="bi bi-plus-circle"></i>
                <span>{t('addNewProduct')}</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {message.text && (
        <div
          className={`alert alert-${message.type} alert-dismissible fade show mb-4`}
          role="alert"
        >
          {message.text}
          <button
            type="button"
            className="btn-close"
            onClick={() => setMessage({ type: "", text: "" })}
            aria-label="Close"
          ></button>
        </div>
      )}

      {showImportModal && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {t('importModalTitle')}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowImportModal(false);
                    setImportFile(null);
                  }}
                  disabled={isImporting}
                ></button>
              </div>
              <div className="modal-body">
                <div
                  className="p-4 text-center"
                  style={{
                    border: "2px dashed #ddd",
                    borderRadius: "8px",
                    cursor: "pointer",
                    transition: "background-color 0.2s",
                  }}
                  onClick={() => fileInputRef.current.click()}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.currentTarget.style.backgroundColor = "#f8f9fa";
                  }}
                  onDragLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.currentTarget.style.backgroundColor = "transparent";
                    handleFileChange({
                      target: { files: e.dataTransfer.files },
                    });
                  }}
                >
                  <input
                    type="file"
                    accept=".json"
                    ref={fileInputRef}
                    className="d-none"
                    onChange={handleFileChange}
                  />
                  {importFile ? (
                    <div>
                      <i className="bi bi-file-earmark-check-fill fs-1 text-success"></i>
                      <p className="mt-2 mb-0">{importFile.name}</p>
                    </div>
                  ) : (
                    <div>
                      <i className="bi bi-cloud-arrow-up fs-1 text-secondary"></i>
                      <p className="mt-2 mb-0">
                        {t('dropOrSelectFile')}
                      </p>
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowImportModal(false);
                    setImportFile(null);
                  }}
                  disabled={isImporting}
                >
                  {t('back')}
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleImport}
                  disabled={!importFile || isImporting}
                >
                  {isImporting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      {t('importing')}
                    </>
                  ) : (
                    t('import')
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="card p-2 bg-light">
        <div className="row g-2 mb-3">
          <div className="col-md-3">
            <input
              className="form-control"
              type="number"
              placeholder={t('minPrice')}
              value={minPriceTemp}
              onChange={(e) => setMinPriceTemp(e.target.value)}
              step="0.01"
              min="0"
            />
          </div>
          <div className="col-md-3">
            <input
              className="form-control"
              type="number"
              placeholder={t('maxPrice')}
              value={maxPriceTemp}
              onChange={(e) => setMaxPriceTemp(e.target.value)}
              step="0.01"
              min="0"
            />
          </div>
          <div className="col-md-3 d-flex align-items-center gap-2">
            <label className="form-label mb-0">{t('category')}:</label>
            <select
              className="form-select"
              value={mainCategoryTemp}
              onChange={handleMainCategoryChange}
              style={{ flex: 1 }}
            >
              <option value="">{t('all')}</option>
              {mainCategories.map((cat) => (
                <option key={cat.id} value={cat.name}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-3 d-flex align-items-center gap-2">
            <label className="form-label mb-0">{t('subcategory')}:</label>
            <select
              className="form-select"
              value={subCategoryTemp}
              onChange={(e) => setSubCategoryTemp(e.target.value)}
              style={{ flex: 1 }}
              disabled={!mainCategoryTemp}
            >
              <option value="">{t('all')}</option>
              {filteredSubCategories.map((cat) => (
                <option key={cat.id} value={cat.name}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="d-flex align-items-center justify-content-between mb-2">
          <div className="d-flex justify-content-between align-items-center w-25">
            <span className="me-2">{t('onlyAvailable')}:</span>
            <label className="switch">
              <input
                type="checkbox"
                checked={availableTemp}
                onChange={(e) => setAvailableTemp(e.target.checked)}
              />
              <span className="slider round"></span>
            </label>
          </div>
          <div className="w-75 d-flex">
            <input
              className="form-control me-2"
              type="text"
              placeholder={t('searchPlaceholder')}
              value={searchTermTemp}
              onChange={(e) => setSearchTermTemp(e.target.value)}
            />
            <button
              className="btn btn-outline-secondary d-flex gap-2 me-2"
              onClick={handleClear}
            >
              <i className="bi bi-x-circle"></i>{t('clear')}
            </button>
            <button
              className="btn btn-outline-primary d-flex gap-2"
              onClick={handleSearch}
            >
              <i className="bi bi-search"></i>{t('search')}
            </button>
          </div>
        </div>

        <div className="table-responsive">
          <table className="table table-light table-hover">
            <thead>
              <tr>
                <th scope="col" style={{ width: "2.5rem" }}>
                  {t('image')}
                </th>
                <th scope="col" style={{ width: "6rem" }}>
                  {t('barcode')}
                </th>
                <th scope="col" style={{ width: "20rem" }}>
                  {t('name')}
                </th>
                <th
                  scope="col"
                  style={{ width: "6rem", cursor: "pointer" }}
                  onClick={() => handleSort("purchase_limit")}
                  className="text-nowrap"
                >
                  {t('stock')}{" "}
                  <i className={`bi ${getSortIcon("purchase_limit") || "bi-chevron-expand"}`}></i>
                </th>
                <th scope="col" style={{ width: "7rem" }}>
                  {t('price')}
                </th>
                <th scope="col" style={{ width: "14rem" }}>
                  {t('category')}
                </th>
                <th scope="col" style={{ width: "14rem" }}>
                  {t('subcategory')}
                </th>
                <th
                  scope="col"
                  style={{ width: "10rem", cursor: "pointer" }}
                  onClick={() => handleSort("created_at")}
                  className="text-nowrap"
                >
                  {t('createdAt')}{" "}
                  <i className={`bi ${getSortIcon("created_at") || "bi-chevron-expand"}`}></i>
                </th>
                <th
                  scope="col"
                  style={{ width: "10rem", cursor: "pointer" }}
                  onClick={() => handleSort("updated_at")}
                  className="text-nowrap"
                >
                  {t('updatedAt')}{" "}
                  <i className={`bi ${getSortIcon("updated_at") || "bi-chevron-expand"}`}></i>
                </th>
              </tr>
            </thead>
            <tbody>
              {currentProducts.length > 0 ? (
                currentProducts.map((product) => {
                  const stockColor = getStockColor(product.purchase_limit);
                  const stockIcon = getStockIcon(product.purchase_limit);
                  return (
                    <tr
                      key={product.id}
                      style={{ fontSize: "14px", cursor: "pointer" }}
                      onClick={() => handleEdit(product.id)}
                    >
                      <td>
                        {product.image_path ? (
                          <img
                            src={`${CONSTANTS.baseRoute}${product.image_path}`}
                            alt={product.name_tm}
                            loading="lazy"
                            style={{
                              width: "40px",
                              height: "32px",
                              objectFit: "contain",
                            }}
                          />
                        ) : (
                          <div
                            style={{
                              width: "40px",
                              height: "32px",
                              backgroundColor: "#f0f0f0",
                              borderRadius: "4px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <i className="bi bi-card-image text-muted"></i>
                          </div>
                        )}
                      </td>
                      <td>{product.barcode || ""}</td>
                      <td>{product.name_tm}</td>
                      <td>
                        <div className="d-flex align-items-center gap-1">
                          <i
                            className={`bi ${stockIcon} text-${stockColor}`}
                          ></i>
                          <span className={`text-${stockColor} fw-bold`}>
                            {product.purchase_limit}
                          </span>
                        </div>
                      </td>
                      <td>{parseFloat(product.price).toFixed(2)} TMT</td>
                      <td>{product.main_category_name}</td>
                      <td>{product.subcategory_name}</td>
                      <td>
                        {product.created_at
                          ? new Date(product.created_at).toLocaleDateString(
                              "tm-TM"
                            )
                          : ""}
                      </td>
                      <td>
                        {product.updated_at
                          ? new Date(product.updated_at).toLocaleDateString(
                              "tm-TM"
                            )
                          : ""}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="9" className="text-center py-4">
                    {t('noProductsFound')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <ProductsPagination
          currentPage={currentPage}
          onPageChange={handlePageChange}
          totalPages={totalPages}
        />
      </div>
    </div>
  );
}